import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { useUserStore } from '@gik/core/store/UserStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import StreamClient, { UserUpdateActivity, UserUpdatesFeedResponse } from '@gik/core/utils/StreamClient';
import { useSearchParam } from 'react-use';
import { AnimatedDivBone, Button, Carousel, Heading, ICarouselProps, UI } from '@gik/ui';
import repeatElement from '@gik/core/utils/repeatElement';
import moment from 'moment';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import AddEditInkindUserUpdatesModal from '@gik/inkind-page/components/AddEditInkindUserUpdatesModal/AddEditInkindUserUpdatesModal';
import InkindUserUpdateCard from '@gik/inkind-page/components/InkindUserUpdateCard/InkindUserUpdateCard';
import Slider from 'react-slick';
import InkindUserUpdatesTimelineModal, {
  InkindUserUpdatesTimelineModalBlockName,
} from '@gik/inkind-page/components/InkindUserUpdatesTimelineModal/InkindUserUpdatesTimelineModal';
import { useUserUpdatesStore } from '@gik/inkind-page/store/UserUpdatesStore';
import shallow from 'zustand/shallow';
import InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import replacePath from '@gik/core/utils/replacePath';
import { auth } from '@gik/auth';
import AddInkindUserUpdateButton from '@gik/inkind-page/components/AddInkindUserUpdateButton/AddInkindUserUpdateButton';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { inkindPageTourSelectors } from '@gik/inkind-page';
import { CalloutBlockType } from '@gik/auth/utils/auth';

export interface IInkindUserUpdatesProps {
  routeId: string;
}

const blockName = 'inkind-user-updates';
const userUpdateDeepLinkParameter = 'userUpdate';

export default function InkindUserUpdates({ routeId }: IInkindUserUpdatesProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const userId = useUserStore(state => state.id);
  const { data: inkindPage } = useInkind(routeId);
  const canEdit = canUserEditPage(inkindPage, userId);
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);

  const { updates, setUpdates } = useUserUpdatesStore(
    state => ({
      updates: state.updates,
      setUpdates: state.setUpdates,
    }),
    shallow
  );

  const [loading, setLoading] = React.useState<boolean>(true);
  const [slideNumber, setSlideNumber] = React.useState<number>(0);
  const closeTimeline = React.useRef<Function>(null);

  const userUpdateParam = useSearchParam(userUpdateDeepLinkParameter);

  React.useEffect(() => {
    if (!userId) {
      // do not retrieve updates if logged out
      setUpdates([]);
      setLoading(false);
    } else {
      (async () => {
        await loadUpdates();
      })();
    }
  }, [userId]);

  async function loadUpdates() {
    const userUpdatesResponse: UserUpdatesFeedResponse = await StreamClient.getUserUpdates(routeId);

    const updates = userUpdatesResponse.results
      .filter(activity => activity.userUpdate && !activity.userUpdateDeleted)
      .map(mapActivityToUserUpdate)
      .sort(updatesSort);

    setUpdates(updates);
    setLoading(false);
  }

  function updatesSort(a: InkindUserUpdate, b: InkindUserUpdate) {
    const da = moment(a.publishedDate, DateTimeUtils.DateFormat);
    const db = moment(b.publishedDate, DateTimeUtils.DateFormat);

    return da.isAfter(db) ? -1 : da.isBefore(db) ? 1 : 0;
  }

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userUpdate = urlParams.get('userUpdate');
    if (userUpdate) {
      const matchingUpdate = updates.find(u => u.id === userUpdate);
      if (matchingUpdate) {
        openTimelineModal(matchingUpdate);
      }
    }
  }, [userUpdateParam, updates]);

  const addEditUpdateModalButtonsRef = React.useRef<HTMLDivElement>();

  async function openAddEditUpdateModal(selectedUpdate?: InkindUserUpdate) {
    await UI.dialog(
      <AddEditInkindUserUpdatesModal
        buttonsPortal={addEditUpdateModalButtonsRef}
        routeId={routeId}
        selectedUpdate={selectedUpdate}
      />,
      {
        title: t(selectedUpdate ? translationKeys.EditUpdateButtonLabel : translationKeys.AddUpdateButtonLabel),
        closable: true,
        buttons: <div ref={addEditUpdateModalButtonsRef} />,
      }
    );

    await loadUpdates();

    carouselRef.current?.slickGoTo(0);
  }

  async function onDeleteUpdate() {
    await loadUpdates();
  }

  React.useEffect(() => {
    if (updates?.length === 0) {
      closeTimeline.current?.();
    }
  }, [updates]);

  async function openTimelineModal(selectedUpdate: InkindUserUpdate) {
    await UI.dialog(
      ({ close }) => {
        closeTimeline.current = close;

        return (
          <InkindUserUpdatesTimelineModal
            openEditUpdateModal={openAddEditUpdateModal}
            onDeleteUpdate={onDeleteUpdate}
            isOrganizer={canEdit}
            selectedUpdate={selectedUpdate}
          />
        );
      },
      {
        title: t(translationKeys.InkindUserUpdatesTimelineModalTitle),
        closable: true,
        contentId: InkindUserUpdatesTimelineModalBlockName,
      }
    );

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(userUpdateDeepLinkParameter);
    replacePath(urlParams);
  }

  const hasUpdates = updates.length > 0;
  const isLoggedIn = !!userId;
  const showTwoCardsNonMobile = !isLoggedIn || canEdit || updates.length > 0;
  const minCards = isMdDown || showTwoCardsNonMobile ? 2 : 1;

  const carouselRef = React.useRef<Slider>();

  const carouselOptions: Partial<ICarouselProps> = {
    ...bem('cards'),
    slidesToShow: isMdDown ? 1.125 : showTwoCardsNonMobile ? 2 : 1,
    infinite: false,
    autoplay: false,
    slidesToScroll: 1,
    arrows: updates.length > (isMdDown ? 1 : 2),
    dots: false,
    beforeChange: (_: never, nextSlide: number) => setSlideNumber(nextSlide),
  };

  function onClickSignInToViewUpdates() {
    auth.loginOrSignup({
      calloutBlock: CalloutBlockType.UPDATES,
      recipientName: inkindPage?.recipientFullName,
    });
  }

  return (
    <div
      {...bem(null, [
        {
          'first-slide': slideNumber === 0,
        },
      ])}
    >
      <div {...bem('title-wrapper')}>
        <Heading level={2} {...bem('title')}>
          {t(translationKeys.InkindUserUpdatesTimelineModalTitle)}
        </Heading>
        {canEdit && hasUpdates && (
          <AddInkindUserUpdateButton id={inkindPageTourSelectors.addUpdate} onClick={() => openAddEditUpdateModal()} />
        )}
      </div>

      <div {...bem('cards-wrapper')}>
        {/* todo could use css to control this element's visibility */}
        {canEdit && !loading && !hasUpdates && (
          <div {...bem('no-updates-button-container')}>
            <AddInkindUserUpdateButton onClick={() => openAddEditUpdateModal()} />
          </div>
        )}

        {/* todo could use css to control this element's visibility */}
        {!isLoggedIn && !loading && inkindPage && (
          <div {...bem('sign-in-button-container')}>
            <Button variant={'secondary'} onClick={onClickSignInToViewUpdates}>
              {t(translationKeys.InkindUserUpdatesSignIn)}
            </Button>
          </div>
        )}

        {loading || !inkindPage ? (
          <div {...bem('skeleton-wrapper')}>
            <AnimatedDivBone {...bem('user-update-card-skeleton')} />
            <AnimatedDivBone {...bem('user-update-card-skeleton')} />
          </div>
        ) : (
          <Carousel ref={carouselRef} {...carouselOptions}>
            {[
              ...updates.map(update => (
                <InkindUserUpdateCard
                  key={update.id}
                  update={update}
                  isOrganiser={canEdit}
                  openAddUpdateModal={openAddEditUpdateModal}
                  openTimelineModal={openTimelineModal}
                />
              )),
              ...(() =>
                updates.length < minCards
                  ? repeatElement(
                      minCards - updates.length,
                      i => (
                        <InkindUserUpdateCard
                          isPlaceholder
                          showAddButton={canEdit && !loading && updates.length === 1}
                          addButtonIndex={updates.length + i}
                          isLoggedIn={isLoggedIn}
                          isOrganiser={canEdit}
                          hasUpdates={hasUpdates}
                          openAddUpdateModal={openAddEditUpdateModal}
                        />
                      ),
                      false
                    )
                  : [])(),
            ]}
          </Carousel>
        )}
      </div>
    </div>
  );
}

export const mapActivityToUserUpdate = (activity: UserUpdateActivity): InkindUserUpdate => ({
  id: activity.id,
  title: activity.userUpdate.title,
  description: activity.userUpdate.description,
  media: Object.values(activity.userUpdateMedia || {}),
  organiserId: activity.actor,
  organiserFullName: activity.userUpdateStatic.organiserFullName,
  publishedDate: activity.userUpdate.publishedDate,
});
